import React, { useEffect, useState } from 'react';
import authService from '../../../Services/authService';
import { X } from "lucide-react";

export default function AddAddressForm({ onBack, onSuccess }) {
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [formErrors, setFormErrors] = useState({});
  const [formData, setFormData] = useState({
    building_name: '',
    state_id: '',
    city_id: '',
    area: '',
    phone_number: '',
    landmark: '',
    pincode: '',
    user_id: authService.getCurrentuser().id,
  });

  const token = authService.getCurrentUsertoken();

  useEffect(() => {
    fetchStates();
  }, []);

  const fetchStates = () => {
    fetch(authService.getApiUrl() + 'states', {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    })
      .then(res => res.json())
      .then(data => setStates(data.data || []))
      .catch(err => console.error(err));
  };

  const fetchCities = (stateId) => {
    fetch(authService.getApiUrl() + 'statebasedCity', {
      method: 'POST',
      body: JSON.stringify({ state_id: stateId }),
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    })
      .then(res => res.json())
      .then(data => setCities(data.data || []))
      .catch(err => console.error(err));
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      const updatedErrors = { ...formErrors };
      delete updatedErrors[field];
      setFormErrors(updatedErrors);
    }
    if (field === 'state_id') {
      fetchCities(value);
      setFormData(prev => ({ ...prev, city_id: '' }));
    }
  };

  const validateForm = () => {
    const errors = {};

    const allowedTextRegex = /^[A-Za-z0-9\s\-\/]+$/;

    // Building Name
    if (!formData.building_name) {
      errors.building_name = "Required";
    } else if (!allowedTextRegex.test(formData.building_name)) {
      errors.building_name = "Only letters, numbers, spaces, hyphen (-), and slash (/) are allowed";
    }

    // Area
    if (!formData.area) {
      errors.area = "Required";
    } else if (!allowedTextRegex.test(formData.area)) {
      errors.area = "Only letters, numbers, spaces, hyphen (-), and slash (/) are allowed";
    }

    // Landmark
    if (!formData.landmark) {
      errors.landmark = "Required";
    } else if (!allowedTextRegex.test(formData.landmark)) {
      errors.landmark = "Only letters, numbers, spaces, hyphen (-), and slash (/) are allowed";
    }

    // State and City
    if (!formData.state_id) errors.state_id = "Select a state";
    if (!formData.city_id) errors.city_id = "Select a city";

    // Pincode
    if (!/^\d{6}$/.test(formData.pincode)) {
      errors.pincode = "Invalid pincode";
    }

    // Phone Number (10 digits, starts with 6–9)
    if (!/^[6-9]\d{9}$/.test(formData.phone_number)) {
      errors.phone_number = "Invalid phone number";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };



  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    fetch(authService.getApiUrl() + 'AddAddress', {
      method: 'POST',
      body: JSON.stringify(formData),
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    })
      .then(res => res.json())
      .then(() => {
        onSuccess(); // Important: trigger parent updates
        setFormData({
          building_name: '',
          state_id: '',
          city_id: '',
          area: '',
          phone_number: '',
          landmark: '',
          pincode: '',
          user_id: authService.getCurrentuser().id,
        });
        setFormErrors({});
      })
      .catch(err => console.error(err.message));
  };

  return (
    <form onSubmit={handleSubmit} className="p-4 space-y-6">
      {/* Building Name */}
      <div>
        <label className="block text-sm font-medium text-[#4A4A4A] mb-2">
          Building Name, Flat Number <span className="text-[#951C25]">*</span>
        </label>
        <input
          type="text"
          className={`w-full px-3 py-2.5 border ${formErrors.building_name ? "border-red-500" : "border-gray-300"} rounded-md`}
          value={formData.building_name}
          onChange={(e) => handleInputChange("building_name", e.target.value)}
        />
        {formErrors.building_name && <p className="text-xs text-red-500 mt-1">{formErrors.building_name}</p>}
      </div>

      {/* Area */}
      <div>
        <label className="block text-sm font-medium text-[#4A4A4A] mb-2">
          Area, Street, Sector <span className="text-[#951C25]">*</span>
        </label>
        <input
          type="text"
          className={`w-full px-3 py-2.5 border ${formErrors.area ? "border-red-500" : "border-gray-300"} rounded-md`}
          value={formData.area}
          onChange={(e) => handleInputChange("area", e.target.value)}
        />
        {formErrors.area && <p className="text-xs text-red-500 mt-1">{formErrors.area}</p>}
      </div>

      {/* Landmark */}
      <div>
        <label className="block text-sm font-medium text-[#4A4A4A] mb-2">
          Landmark <span className="text-[#951C25]">*</span>
        </label>
        <input
          type="text"
          className={`w-full px-3 py-2.5 border ${formErrors.landmark ? "border-red-500" : "border-gray-300"} rounded-md`}
          value={formData.landmark}
          onChange={(e) => handleInputChange("landmark", e.target.value)}
        />
        {formErrors.landmark && <p className="text-xs text-red-500 mt-1">{formErrors.landmark}</p>}
      </div>

      {/* State */}
      <div>
        <label className="block text-sm font-medium text-[#4A4A4A] mb-2">
          State <span className="text-[#951C25]">*</span>
        </label>
        <select
          className={`w-full px-3 py-2.5 border ${formErrors.state_id ? "border-red-500" : "border-gray-300"} rounded-md`}
          value={formData.state_id}
          onChange={(e) => handleInputChange("state_id", e.target.value)}
        >
          <option value="">--Select--</option>
          {states.map((item) => (
            <option key={item.id} value={item.id}>{item.state_name}</option>
          ))}
        </select>
        {formErrors.state_id && <p className="text-xs text-red-500 mt-1">{formErrors.state_id}</p>}
      </div>

      {/* City */}
      <div>
        <label className="block text-sm font-medium text-[#4A4A4A] mb-2">
          City <span className="text-[#951C25]">*</span>
        </label>
        <select
          className={`w-full px-3 py-2.5 border ${formErrors.city_id ? "border-red-500" : "border-gray-300"} rounded-md`}
          value={formData.city_id}
          onChange={(e) => handleInputChange("city_id", e.target.value)}
        >
          <option value="">--Select--</option>
          {cities.map((item) => (
            <option key={item.id} value={item.id}>{item.city_name}</option>
          ))}
        </select>
        {formErrors.city_id && <p className="text-xs text-red-500 mt-1">{formErrors.city_id}</p>}
      </div>

      {/* Phone Number & Pincode */}
      {/* <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-[#4A4A4A] mb-2">
            Phone Number <span className="text-[#951C25]">*</span>
          </label>
          <input
            type="text"
            className={`w-full px-3 py-2.5 border ${formErrors.phone_number ? "border-red-500" : "border-gray-300"} rounded-md`}
            value={formData.phone_number}
            onChange={(e) => handleInputChange("phone_number", e.target.value)}
          />
          {formErrors.phone_number && <p className="text-xs text-red-500 mt-1">{formErrors.phone_number}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-[#4A4A4A] mb-2">
            Pincode <span className="text-[#951C25]">*</span>
          </label>
          <input
            type="text"
            className={`w-full px-3 py-2.5 border ${formErrors.pincode ? "border-red-500" : "border-gray-300"} rounded-md`}
            value={formData.pincode}
            onChange={(e) => handleInputChange("pincode", e.target.value)}
          />
          {formErrors.pincode && <p className="text-xs text-red-500 mt-1">{formErrors.pincode}</p>}
        </div>
      </div> */}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Phone Number */}
        <div>
          <label className="block text-sm font-medium text-[#4A4A4A] mb-2">
            Phone Number <span className="text-[#951C25]">*</span>
          </label>
          <div className={`flex rounded-md border ${formErrors.phone_number ? "border-red-500" : "border-gray-300"} focus-within:ring-1 focus-within:ring-[#951C25] focus-within:border-[#951C25]`}>
            <span className="flex items-center px-3 bg-[#F9F9F9] text-sm text-[#4A4A4A]/70 border-r border-gray-300 rounded-l-md">
              +91
            </span>
            <input
              type="text"
              maxLength={10}
              className="w-full px-3 py-2.5 rounded-r-md focus:outline-none"
              value={formData.phone_number || ""}
              onChange={(e) =>
                handleInputChange("phone_number", e.target.value.replace(/\D/g, ""))
              }
            />
          </div>
          {formErrors.phone_number && (
            <p className="text-xs text-red-500 mt-1">{formErrors.phone_number}</p>
          )}
        </div>

        {/* Pincode */}
        <div>
          <label className="block text-sm font-medium text-[#4A4A4A] mb-2">
            Pincode <span className="text-[#951C25]">*</span>
          </label>
          <input
            type="text"
            maxLength={6}
            className={`w-full px-3 py-2.5 border ${formErrors.pincode ? "border-red-500" : "border-gray-300"} rounded-md focus:outline-none focus:ring-1 focus:ring-[#951C25] focus:border-[#951C25]`}
            value={formData.pincode || ""}
            onChange={(e) =>
              handleInputChange("pincode", e.target.value.replace(/\D/g, ""))
            }
          />
          {formErrors.pincode && (
            <p className="text-xs text-red-500 mt-1">{formErrors.pincode}</p>
          )}
        </div>
      </div>


      {/* Action Buttons */}
      <div className="flex justify-between pt-4">
        <button type="button" onClick={onBack} className="px-4 py-2 border border-gray-400 text-gray-700 rounded-md hover:bg-gray-100">
          Cancel
        </button>
        <button type="submit" className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700">
          Add Address
        </button>
      </div>
    </form>
  );
}
